/**
 * The donor portal's tabs (stop 2B): Overview, Gifts, Monthly gifts, Statements, Your details, Stay in touch.
 *
 * The page holds every panel; this shows one at a time with the ARIA tab pattern (a tablist of buttons, arrow keys
 * and Home/End move between them, the panel of the selected tab is the only one shown). A tab is deep-linkable:
 * `#gifts` opens Gifts, and the address follows the tab as it changes. The last tab is remembered in this browser.
 * Without this script the tab bar stays hidden and every panel is shown one under another, which is a page that works.
 */
import { attachEmailSuggestion } from './shared/email-suggest.js';

( function () {
	'use strict';

	const STORAGE_KEY = 'anudanPortalTab';

	/**
	 * @return {string} The last tab this browser had open, or '' when storage is not available (private windows, blocked site data).
	 */
	function remembered() {
		try {
			return window.localStorage.getItem( STORAGE_KEY ) || '';
		} catch ( error ) {
			return '';
		}
	}

	/**
	 * @param {string} name The tab to remember.
	 */
	function remember( name ) {
		try {
			window.localStorage.setItem( STORAGE_KEY, name );
		} catch ( error ) {
			// Nothing to do: the tab is simply not remembered.
		}
	}

	/**
	 * @param {HTMLElement} root The portal's root element.
	 */
	function init( root ) {
		const bar = root.querySelector( '[data-anudan-tabs]' );
		const tabs = Array.from( root.querySelectorAll( '[role="tab"][data-anudan-tab]' ) );

		if ( ! bar || 0 === tabs.length ) {
			return;
		}

		const panelOf = ( tab ) => document.getElementById( tab.getAttribute( 'aria-controls' ) );
		const names = tabs.map( ( tab ) => tab.getAttribute( 'data-anudan-tab' ) );

		bar.hidden = false;

		/**
		 * Shows one tab's panel and hides the rest.
		 *
		 * @param {string}  name  The tab's name.
		 * @param {boolean} focus Whether to move focus to the tab (a keyboard move).
		 * @param {boolean} write Whether to update the address and the memory.
		 */
		const select = ( name, focus, write ) => {
			const chosen = tabs.find( ( tab ) => tab.getAttribute( 'data-anudan-tab' ) === name ) || tabs[ 0 ];
			const moved = 'true' !== chosen.getAttribute( 'aria-selected' );

			tabs.forEach( ( tab ) => {
				const on = tab === chosen;
				const panel = panelOf( tab );

				tab.setAttribute( 'aria-selected', on ? 'true' : 'false' );
				tab.tabIndex = on ? 0 : -1;
				tab.classList.toggle( 'is-current', on );

				if ( panel ) {
					panel.hidden = ! on;
				}
			} );

			if ( focus ) {
				chosen.focus();
			}

			if ( write ) {
				const key = chosen.getAttribute( 'data-anudan-tab' );

				remember( key );
				// Item 6 after rc5: a "saved" message belongs to the tab it was saved on and goes when the donor moves to another.
				if ( moved ) {
					document.dispatchEvent( new CustomEvent( 'anudan:portal-tab', { detail: { tab: key } } ) );
				}

				if ( window.history && window.history.replaceState ) {
					window.history.replaceState( null, '', `${ window.location.pathname }${ window.location.search }#${ key }` );
				}
			}

			// Keep the chosen tab in view when the bar scrolls sideways (a phone).
			chosen.scrollIntoView( { block: 'nearest', inline: 'nearest' } );
		};

		tabs.forEach( ( tab, index ) => {
			tab.addEventListener( 'click', () => select( tab.getAttribute( 'data-anudan-tab' ), false, true ) );

			tab.addEventListener( 'keydown', ( event ) => {
				let target = null;

				if ( 'ArrowRight' === event.key ) {
					target = ( index + 1 ) % tabs.length;
				} else if ( 'ArrowLeft' === event.key ) {
					target = ( index - 1 + tabs.length ) % tabs.length;
				} else if ( 'Home' === event.key ) {
					target = 0;
				} else if ( 'End' === event.key ) {
					target = tabs.length - 1;
				}

				if ( null !== target ) {
					event.preventDefault();
					select( names[ target ], true, true );
				}
			} );
		} );

		// A link inside the page that names a tab ("See your monthly gifts") opens it.
		root.querySelectorAll( '[data-anudan-tab-link]' ).forEach( ( link ) => {
			link.addEventListener( 'click', ( event ) => {
				event.preventDefault();
				select( link.getAttribute( 'data-anudan-tab-link' ) || '', true, true );
			} );
		} );

		// A fade at the edge of a bar that scrolls sideways, as the admin's tab bars have.
		const list = bar.querySelector( '.anudan-tabs__bar' );

		if ( list ) {
			const fade = () => {
				bar.classList.toggle( 'is-scrollable-end', list.scrollWidth - list.clientWidth - list.scrollLeft > 4 );
				bar.classList.toggle( 'is-scrollable-start', list.scrollLeft > 4 );
			};

			list.addEventListener( 'scroll', fade, { passive: true } );
			window.addEventListener( 'resize', fade );
			fade();
		}

		// The address first (a deep link); then the tab holding a message the server printed (a confirmation link's thanks,
		// a failed save), so it is never left in a hidden panel; then the last tab this browser had open; then the Overview.
		const fromAddress = window.location.hash.replace( /^#/, '' );
		// A live region the script itself shows later (the Gifts tab's loading line and its error box) sits hidden, and is no message.
		const shown = ( panel, element ) => {
			const hiddenAncestor = element.closest( '[hidden]' );

			return ! hiddenAncestor || ! panel.contains( hiddenAncestor ) || hiddenAncestor === panel;
		};
		const messagePanel = Array.from( root.querySelectorAll( '[role="tabpanel"]' ) ).find( ( panel ) => Array.from( panel.querySelectorAll( '[role="status"]:not(:empty, .anudan-portal__notice--standing), [role="alert"]:not(:empty)' ) ).some( ( element ) => shown( panel, element ) ) );
		const messageTab = messagePanel ? messagePanel.id.replace( /^anudan-portal-panel-/, '' ) : '';
		// R2-18h (rc2): an address naming a part of a panel (a form's redirect back to the email section) opens that panel.
		const target = fromAddress && ! names.includes( fromAddress ) ? document.getElementById( fromAddress ) : null;
		const targetPanel = target ? target.closest( '[role="tabpanel"]' ) : null;
		const targetTab = targetPanel && root.contains( targetPanel ) ? targetPanel.id.replace( /^anudan-portal-panel-/, '' ) : '';
		const start = names.includes( fromAddress )
			? fromAddress
			: names.includes( targetTab ) ? targetTab : names.includes( messageTab ) ? messageTab : names.includes( remembered() ) ? remembered() : names[ 0 ];

		select( start, false, false );

		// The browser could not scroll to a part of a panel that was hidden when the page loaded; now it is shown.
		if ( target && targetTab === start ) {
			target.scrollIntoView( { block: 'start' } );
		}

		window.addEventListener( 'hashchange', () => {
			const name = window.location.hash.replace( /^#/, '' );

			if ( names.includes( name ) ) {
				select( name, false, true );
			}
		} );
	}

	/**
	 * P1 (0.9.40): the Gifts tab's filters apply as they are chosen, without leaving the tab. The page is asked for again with the
	 * new filters, as the filter form itself would ask, and the list part of it replaces this one; meanwhile the list shows it is
	 * loading, and if the page cannot be reached it says so with a "Try again". Without this script the form's own button does
	 * the same with a page load.
	 *
	 * @param {HTMLElement} panel The Gifts panel.
	 */
	function initGifts( panel ) {
		const form = panel.querySelector( '[data-anudan-gift-filters]' );
		const results = panel.querySelector( '[data-anudan-gift-results]' );
		const loading = panel.querySelector( '[data-anudan-gift-loading]' );
		const failure = panel.querySelector( '[data-anudan-gift-error]' );
		const retry = panel.querySelector( '[data-anudan-gift-retry]' );
		let last = '';
		let asked = 0;

		if ( ! results || ! loading || ! failure ) {
			return;
		}

		const show = ( state ) => {
			results.hidden = 'results' !== state;
			loading.hidden = 'loading' !== state;
			failure.hidden = 'error' !== state;
		};

		const load = async ( url ) => {
			const ask = ++asked;

			last = url;
			show( 'loading' );

			try {
				const response = await fetch( url, { credentials: 'same-origin', headers: { Accept: 'text/html' } } );

				if ( ! response.ok ) {
					throw new Error( String( response.status ) );
				}

				const page = new DOMParser().parseFromString( await response.text(), 'text/html' );
				const fresh = page.querySelector( '[data-anudan-gift-results]' );

				if ( ! fresh ) {
					throw new Error( 'no list' );
				}

				if ( ask !== asked ) {
					return;
				}

				results.replaceChildren( ...Array.from( fresh.childNodes ).map( ( node ) => document.importNode( node, true ) ) );
				wireLinks();
				show( 'results' );

				if ( window.history && window.history.replaceState ) {
					window.history.replaceState( null, '', url );
				}
			} catch ( error ) {
				if ( ask === asked ) {
					show( 'error' );
				}
			}
		};

		const wireLinks = () => {
			results.querySelectorAll( '[data-anudan-gift-page], [data-anudan-gift-reset]' ).forEach( ( link ) => {
				link.addEventListener( 'click', ( event ) => {
					event.preventDefault();

					if ( form && link.hasAttribute( 'data-anudan-gift-reset' ) ) {
						form.reset();
						form.querySelectorAll( 'select' ).forEach( ( select ) => {
							select.value = '';
						} );
					}

					load( link.href );
				} );
			} );
		};

		if ( form ) {
			const button = form.querySelector( '[data-anudan-filter-submit]' );

			if ( button ) {
				button.hidden = true;
			}

			const submit = () => {
				const query = new URLSearchParams();

				Array.from( new FormData( form ).entries() ).forEach( ( [ key, value ] ) => {
					if ( '' !== String( value ) ) {
						query.append( key, String( value ) );
					}
				} );

				const base = form.getAttribute( 'action' ) || window.location.pathname;
				const url = new URL( base, window.location.href );

				url.search = query.toString();
				url.hash = 'gifts';
				load( url.toString() );
			};

			form.addEventListener( 'change', submit );
			form.addEventListener( 'submit', ( event ) => {
				event.preventDefault();
				submit();
			} );
		}

		if ( retry ) {
			retry.addEventListener( 'click', () => {
				if ( '' !== last ) {
					load( last );
				}
			} );
		}

		wireLinks();
	}

	document.querySelectorAll( '[data-anudan-portal-tabs]' ).forEach( init );
	document.querySelectorAll( '[data-anudan-portal-gifts]' ).forEach( initGifts );

	// R2-09 (rc2): "Did you mean name@gmail.com?" under the box for a new email address.
	const __ = ( text ) => ( window.wp && window.wp.i18n ? window.wp.i18n.__( text, 'anudan' ) : text );

	document.querySelectorAll( '.anudan-portal-profile__section input[type="email"][name="email"]' ).forEach( ( input ) => attachEmailSuggestion( input, __ ) );
}() );
